import { useCallback, useEffect, useState } from 'react';
import throttle from 'lodash/throttle';

export type WidthComparator = 'eq' | 'gt' | 'gte' | 'lt' | 'lte' | 'between';

export interface UseMatchWidthOptions {
  throttleMs?: number;
  breakpoints?: Record<string, number>;
}

export type IsWidthFn = (
  target: number | string | [number, number],
  comparator?: WidthComparator,
) => boolean;

const useWindowWidth = (throttleMs: number): number => {
  const [width, setWidth] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onResize = throttle(() => {
      setWidth(window.innerWidth);
    }, throttleMs);

    onResize();

    window.addEventListener('resize', onResize, { passive: true });
    return () => {
      window.removeEventListener('resize', onResize);
      (onResize as any).cancel?.();
    };
  }, [throttleMs]);

  return width;
};

const resolveTarget = (
  target: number | string,
  breakpoints?: Record<string, number>,
): number => {
  if (typeof target === 'number') return target;

  const fromBp = breakpoints?.[target];
  if (typeof fromBp === 'number') return fromBp;

  const parsed = Number(target);
  if (!Number.isNaN(parsed)) return parsed;

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `[useMatchWidth] Target inválido "${target}". Use número em px ou chave de breakpoint fornecida.`,
    );
  }
  return NaN;
};

export const useMatchWidth = (options?: UseMatchWidthOptions): IsWidthFn => {
  const { throttleMs = 100, breakpoints } = options ?? {};
  const width = useWindowWidth(throttleMs);

  const isWidth: IsWidthFn = useCallback(
    (target, comparator: WidthComparator = 'eq') => {
      if (comparator === 'between') {
        const [min, max] = Array.isArray(target) ? target : [NaN, NaN];
        const minPx =
          typeof min === 'string' ? resolveTarget(min, breakpoints) : min;
        const maxPx =
          typeof max === 'string' ? resolveTarget(max, breakpoints) : max;
        if (Number.isNaN(minPx) || Number.isNaN(maxPx)) return false;
        return width >= minPx && width <= maxPx;
      }

      const px =
        typeof target === 'string'
          ? resolveTarget(target, breakpoints)
          : (target as number);
      if (Number.isNaN(px)) return false;

      switch (comparator) {
        case 'eq':
          return width === px;
        case 'gt':
          return width > px;
        case 'gte':
          return width >= px;
        case 'lt':
          return width < px;
        case 'lte':
          return width <= px;
        default:
          return false;
      }
    },
    [breakpoints, width],
  );

  return isWidth;
};

export const useIsWidth = (
  target: number | string | [number, number],
  comparator: WidthComparator = 'eq',
  options?: UseMatchWidthOptions,
): boolean => {
  const isWidth = useMatchWidth(options);
  return isWidth(target, comparator);
};

export default useMatchWidth;
